// QuickActionModal — inline popup for dashboard Quick Action buttons.
// Instead of navigating away, an action opens a lightweight form right on
// the dashboard, submits via the API, and closes on success.
//
// Supported actions:
//   • employee   → create invite + show invite code
//   • location   → new location (name + color)
//   • team       → new team (name)
//   • broadcast  → send a message to everyone in the company
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Users, MapPin, Building2, Megaphone, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import InviteCodeModal from "@/components/InviteCodeModal";
import SuccessCheckmark from "@/components/SuccessCheckmark";

const ROLE_OPTIONS = [
  { value: "employee", key: "role_employee" },
  { value: "teamlead", key: "role_teamlead" },
  { value: "manager", key: "role_manager" },
];
const LOCATION_COLORS = ["#2563EB", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#EC4899"];

const META = {
  employee: { icon: Users, titleKey: "add_employee" },
  location: { icon: MapPin, titleKey: "add_location" },
  team: { icon: Building2, titleKey: "add_team" },
  broadcast: { icon: Megaphone, titleKey: "broadcast" },
};

export default function QuickActionModal({ open, action, onClose }) {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const [role, setRole] = useState("employee");
  const [locName, setLocName] = useState("");
  const [locColor, setLocColor] = useState(LOCATION_COLORS[0]);
  const [teamName, setTeamName] = useState("");
  const [bcTitle, setBcTitle] = useState("");
  const [bcBody, setBcBody] = useState("");
  const [invite, setInvite] = useState(null); // {code, role, joinUrl}
  const [successLabel, setSuccessLabel] = useState(null); // when set, we render
  // the Apple-like checkmark overlay for ~1.1s before closing the modal.

  useEffect(() => {
    if (!open) {
      setBusy(false); setRole("employee"); setLocName(""); setLocColor(LOCATION_COLORS[0]);
      setTeamName(""); setBcTitle(""); setBcBody(""); setInvite(null); setSuccessLabel(null);
    }
  }, [open]);

  const finishWithSuccess = (label) => {
    // Show the tick, then close + refresh dashboard so newly added rows appear.
    setSuccessLabel(label);
    setTimeout(() => {
      window.dispatchEvent(new Event("rostr:refresh-dashboard"));
      setSuccessLabel(null);
      onClose();
    }, 1100);
  };

  const submit = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (action === "employee") {
        const { data } = await api.post("/invitations", { role });
        const origin = window.location.origin;
        setInvite({ code: data.code, role, joinUrl: `${origin}/join?code=${data.code}` });
      } else if (action === "location") {
        if (!locName.trim()) throw new Error(t("field_required"));
        await api.post("/locations", { name: locName.trim(), color: locColor });
        finishWithSuccess(t("location_created") || "Locatie toegevoegd");
      } else if (action === "team") {
        if (!teamName.trim()) throw new Error(t("field_required"));
        await api.post("/teams", { name: teamName.trim() });
        finishWithSuccess(t("team_created") || "Team toegevoegd");
      } else if (action === "broadcast") {
        if (!bcTitle.trim() || !bcBody.trim()) throw new Error(t("field_required"));
        await api.post("/notifications/broadcast", { title: bcTitle.trim(), body: bcBody.trim() });
        finishWithSuccess(t("broadcast_sent") || "Bericht verstuurd");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || e.message || t("error"));
    } finally {
      setBusy(false);
    }
  };

  const meta = META[action] || {};
  const Icon = meta.icon || Users;

  return (
    <>
      <AnimatePresence>
        {open && !invite && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[55] bg-black/50 backdrop-blur-sm grid place-items-center p-4"
            onClick={successLabel ? undefined : onClose}
            data-testid="quick-action-modal"
          >
            <motion.div
              initial={{ scale: 0.94, opacity: 0, y: 12 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.94, opacity: 0 }}
              transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
              className="card w-full max-w-md p-0 overflow-hidden relative"
              onClick={(e) => e.stopPropagation()}
            >
              {successLabel && (
                <motion.div
                  key="qa-success"
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                  className="absolute inset-0 z-10 grid place-items-center bg-[hsl(var(--surface))] py-10"
                  data-testid="qa-success"
                >
                  <SuccessCheckmark label={successLabel} />
                </motion.div>
              )}
              <div className="relative px-6 pt-6 pb-4 flex items-start gap-3 border-b hairline">
                <div className="w-11 h-11 rounded-2xl bg-[hsl(var(--primary-soft))] text-[hsl(var(--primary))] grid place-items-center shrink-0">
                  <Icon size={18} strokeWidth={2} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-display text-[17px] font-bold text-[hsl(var(--text-strong))] leading-tight">
                    {t(meta.titleKey) || "Actie"}
                  </h3>
                  <p className="text-[12px] text-[hsl(var(--text-muted))] mt-0.5">
                    {t("qa_desc_" + action) || t("quick_add_desc") || "Snel toevoegen zonder de pagina te verlaten"}
                  </p>
                </div>
                <button onClick={onClose} className="w-8 h-8 grid place-items-center rounded-full hover:bg-[hsl(var(--muted))] transition-colors" aria-label={t("close")} data-testid="qa-close">
                  <X size={14} strokeWidth={2} />
                </button>
              </div>

              <div className="px-6 py-5 space-y-4">
                {action === "employee" && (
                  <div>
                    <label className="text-[12px] font-semibold text-[hsl(var(--text-muted))] uppercase tracking-wider mb-2 block">{t("role")}</label>
                    <div className="grid grid-cols-3 gap-2" data-testid="qa-role-picker">
                      {ROLE_OPTIONS.map((r) => (
                        <button
                          key={r.value}
                          type="button"
                          onClick={() => setRole(r.value)}
                          className={`px-3 h-10 rounded-xl border text-[13px] font-semibold transition-all ${
                            role === r.value
                              ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary-soft))] text-[hsl(var(--primary))]"
                              : "hairline hover:bg-[hsl(var(--muted))] text-[hsl(var(--text-muted))]"
                          }`}
                          data-testid={`qa-role-${r.value}`}
                        >
                          {t(r.key)}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {action === "location" && (
                  <>
                    <div>
                      <label className="text-[12px] font-semibold text-[hsl(var(--text-muted))] uppercase tracking-wider mb-2 block">{t("name")}</label>
                      <input value={locName} onChange={(e) => setLocName(e.target.value)} className="input-field w-full" placeholder="Bijv. Hoofdvestiging" autoFocus data-testid="qa-loc-name" />
                    </div>
                    <div>
                      <label className="text-[12px] font-semibold text-[hsl(var(--text-muted))] uppercase tracking-wider mb-2 block">{t("color")}</label>
                      <div className="flex gap-2">
                        {LOCATION_COLORS.map((c) => (
                          <button key={c} type="button" onClick={() => setLocColor(c)}
                            className={`w-9 h-9 rounded-xl border-2 transition-all ${locColor === c ? "scale-110 border-[hsl(var(--text-strong))]" : "border-transparent"}`}
                            style={{ background: c }} data-testid={`qa-loc-color-${c}`} aria-label={c} />
                        ))}
                      </div>
                    </div>
                  </>
                )}
                {action === "team" && (
                  <div>
                    <label className="text-[12px] font-semibold text-[hsl(var(--text-muted))] uppercase tracking-wider mb-2 block">{t("name")}</label>
                    <input value={teamName} onChange={(e) => setTeamName(e.target.value)} className="input-field w-full" placeholder="Bijv. Keuken" autoFocus data-testid="qa-team-name" />
                  </div>
                )}
                {action === "broadcast" && (
                  <>
                    <div>
                      <label className="text-[12px] font-semibold text-[hsl(var(--text-muted))] uppercase tracking-wider mb-2 block">{t("title")}</label>
                      <input value={bcTitle} onChange={(e) => setBcTitle(e.target.value)} className="input-field w-full" placeholder={t("broadcast_title_ph") || "Onderwerp"} autoFocus data-testid="qa-bc-title" />
                    </div>
                    <div>
                      <label className="text-[12px] font-semibold text-[hsl(var(--text-muted))] uppercase tracking-wider mb-2 block">{t("message")}</label>
                      <textarea value={bcBody} onChange={(e) => setBcBody(e.target.value)} rows={4} className="input-field w-full resize-none" placeholder={t("broadcast_body_ph") || "Wat wil je iedereen laten weten?"} data-testid="qa-bc-body" />
                    </div>
                  </>
                )}
              </div>

              <div className="flex items-center justify-end gap-2 px-6 py-4 border-t hairline bg-[hsl(var(--bg))]">
                <button onClick={onClose} className="btn btn-ghost">{t("cancel")}</button>
                <button onClick={submit} disabled={busy} className="btn btn-primary" data-testid="qa-submit">
                  {busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                  {t("save") || "Opslaan"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {invite && (
        <InviteCodeModal
          open={!!invite}
          code={invite.code}
          role={invite.role}
          joinUrl={invite.joinUrl}
          onClose={() => { setInvite(null); onClose(); }}
        />
      )}
    </>
  );
}
